const SITE_ORIGIN = 'https://www.gograbjob.com';

export interface CareerPostSeoFields {
  slug: string;
  title: string;
  meta_description: string | null;
  hero_intro: string | null;
  description: string | null;
  location: string | null;
  job_type: string | null;
  published_at: string | null;
  closes_at: string | null;
}

function mapEmploymentType(jobType: string | null): string | undefined {
  if (!jobType) return undefined;
  const t = jobType.toLowerCase();
  if (t.includes('intern')) return 'INTERN';
  if (t.includes('part')) return 'PART_TIME';
  if (t.includes('contract') || t.includes('freelance')) return 'CONTRACTOR';
  if (t.includes('temp')) return 'TEMPORARY';
  return 'FULL_TIME';
}

function jobDescription(post: CareerPostSeoFields): string {
  const text =
    post.meta_description?.trim() ||
    post.hero_intro?.trim() ||
    post.description?.trim() ||
    `Apply for ${post.title} at GoGrabJob.`;
  return text.slice(0, 5000);
}

/** schema.org JobPosting for career detail pages. */
export function buildJobPostingJsonLd(post: CareerPostSeoFields): Record<string, unknown> {
  const isRemote = (post.location ?? '').toLowerCase().includes('remote');
  const applyUrl = `${SITE_ORIGIN}/careers/${post.slug}/apply`;

  const ld: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: post.title,
    description: jobDescription(post),
    identifier: {
      '@type': 'PropertyValue',
      name: 'GoGrabJob',
      value: post.slug,
    },
    datePosted: post.published_at ?? undefined,
    validThrough: post.closes_at ?? undefined,
    employmentType: mapEmploymentType(post.job_type),
    hiringOrganization: {
      '@type': 'Organization',
      name: 'GoGrabJob',
      sameAs: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/og-default.png`,
    },
    directApply: true,
    url: `${SITE_ORIGIN}/careers/${post.slug}`,
  };

  if (isRemote) {
    ld.jobLocationType = 'TELECOMMUTE';
    ld.applicantLocationRequirements = {
      '@type': 'Country',
      name: 'India',
    };
  }

  ld.jobLocation = {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN',
      addressLocality: isRemote ? 'Remote' : (post.location ?? 'India'),
    },
  };

  ld.potentialAction = {
    '@type': 'ApplyAction',
    target: applyUrl,
  };

  return ld;
}

export function buildCareersListingJsonLd(
  posts: Array<{ slug: string; title: string; published_at: string | null }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Careers at GoGrabJob',
    url: `${SITE_ORIGIN}/careers`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_ORIGIN}/careers/${post.slug}`,
        name: post.title,
      })),
    },
  };
}
